import { useCallback, useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { agregarLey24476, eliminarLey24476, listarLey24476 } from '../../services/aperturaPlanService';
import { aFechaPeriodo, aMesPeriodo, mesAnio, mesesDePeriodo, periodoValido } from './EditorPeriodos24476';

const mesesDe = (p) => mesesDePeriodo({ desde: aMesPeriodo(p.desde), hasta: aMesPeriodo(p.hasta) });

/**
 * Períodos de la Ley 24.476 que compró el cliente (V267). Son aportes del cliente: se cargan solos
 * al abrir el plan de pago y acá se pueden agregar o quitar a mano. La calculadora los toma como
 * "Ley 24.476 ya comprada".
 */
export default function SeccionLey24476Cliente({ cuil }) {
    const [periodos, setPeriodos] = useState([]);
    const [nuevo, setNuevo] = useState({ desde: '', hasta: '' });
    const [guardando, setGuardando] = useState(false);

    const cargar = useCallback(() => {
        if (!cuil) return;
        listarLey24476(cuil)
            .then((data) => setPeriodos(Array.isArray(data) ? data : []))
            .catch(() => setPeriodos([]));
    }, [cuil]);

    useEffect(() => { cargar(); }, [cargar]);

    const agregar = async () => {
        setGuardando(true);
        try {
            setPeriodos(await agregarLey24476(cuil, { desde: aFechaPeriodo(nuevo.desde), hasta: aFechaPeriodo(nuevo.hasta) }));
            setNuevo({ desde: '', hasta: '' });
        } catch (e) {
            toast.error(e?.response?.data?.message || 'No se pudo agregar el período');
        } finally {
            setGuardando(false);
        }
    };

    const quitar = async (p) => {
        if (!window.confirm(`¿Quitar el período ${mesAnio(p.desde)} – ${mesAnio(p.hasta)}?`)) return;
        try {
            setPeriodos(await eliminarLey24476(cuil, p.id));
        } catch (e) {
            toast.error(e?.response?.data?.message || 'No se pudo quitar el período');
        }
    };

    const total = periodos.reduce((acc, p) => acc + mesesDe(p), 0);

    return (
        <Accordion defaultExpanded={periodos.length > 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" sx={{ color: '#1e3a8a' }} fontWeight="bold">
                        Ley 24.476 comprada
                    </Typography>
                    {total > 0 && <Chip size="small" color="primary" variant="outlined" label={`${total} meses`} />}
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={2}>
                    {periodos.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Sin períodos. Se cargan solos al abrir el plan de pago, o se agregan acá.
                        </Typography>
                    ) : (
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {periodos.map((p) => (
                                <Chip
                                    key={p.id}
                                    label={`${mesAnio(p.desde)} – ${mesAnio(p.hasta)} · ${mesesDe(p)} meses`}
                                    onDelete={() => quitar(p)}
                                    deleteIcon={<DeleteIcon aria-label={`Quitar ${mesAnio(p.desde)}`} />}
                                />
                            ))}
                        </Box>
                    )}
                    <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
                        <TextField size="small" type="month" label="Desde" InputLabelProps={{ shrink: true }}
                            value={nuevo.desde} onChange={(e) => setNuevo((n) => ({ ...n, desde: e.target.value }))} />
                        <TextField size="small" type="month" label="Hasta" InputLabelProps={{ shrink: true }}
                            value={nuevo.hasta} onChange={(e) => setNuevo((n) => ({ ...n, hasta: e.target.value }))} />
                        <Button variant="outlined" size="small" onClick={agregar} disabled={guardando || !periodoValido(nuevo)}>
                            Agregar período
                        </Button>
                    </Box>
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
}
